<script lang="tsx">
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import { ElMain, ElHeader, ElFooter } from 'element-plus';

import { getColStyle, getColClassList } from './utils';
import { ArrowDown, ArrowUp } from '@element-plus/icons-vue';

export default defineComponent({
  name: 'AppPage',
  props: {
    mainBg: {
      type: String,
      default: '#fff',
    },
    mainPadding: {
      type: String,
      default: '16px 24px'
    },
    laside: {
      type: Object,
      default() {
        return {};
      },
    },

    raside: {
      type: Object,
      default() {
        return {};
      },
    },

    fheader: {
      type: Object,
      default() {
        return {};
      },
    },
    fheaderBg: {
      type: String,
      default: '#fff',
    },
    fheaderPadding: {
      type: String,
      default: '15px 10px 0'
    },
  },

  setup() {
    const contentHeight = ref(0);
    const collapse = ref(false);

    const contentMainRef = ref<typeof ElMain | null>(null);
    const contentHeaderRef = ref<typeof ElHeader | null>(null);
    const contentFooterRef = ref<typeof ElFooter | null>(null);
    const contentWrapperRef = ref<typeof ElMain | null>(null);

    const computedContentHeight = () => {
      if (contentMainRef.value) {
        const { clientHeight } = contentMainRef.value.$el;
        let headerHeight = 0;
        let footerHeight = 0;
        if (contentHeaderRef.value) {
          headerHeight = parseInt(getComputedStyle(contentHeaderRef.value?.$el)?.height, 10);
        }
        if (contentFooterRef.value) {
          footerHeight = parseInt(getComputedStyle(contentFooterRef.value?.$el)?.height, 10);
        }
        contentHeight.value = clientHeight - headerHeight - footerHeight - 1;
      }
    };

    let timer: number | null = null;
    const windowResizeCallBack = () => {
      if (timer) return;
      timer = window.setTimeout(() => {
        timer = null;
        computedContentHeight();
      }, 200);
    };
    onMounted(() => {
      setTimeout(() => {
        computedContentHeight();
      });
      window.addEventListener('resize', windowResizeCallBack, false);
    });
    onUnmounted(() => {
      window.removeEventListener('resize', windowResizeCallBack, false);
    });

    return {
      contentHeight,
      collapse,

      contentMainRef,
      contentHeaderRef,
      contentWrapperRef,
      contentFooterRef,
      computedContentHeight,
      windowResizeCallBack,
    };
  },
  render() {
    const renderCollapse = () => {
      const { showCollapse } = this.fheader;
      return (
        <el-button
          v-show={showCollapse}
          type='primary'
          link
          onClick={() => {
            this.collapse = !this.collapse;
            this.windowResizeCallBack();
          }}
        >
          {this.collapse ? '收起' : '展开'}

          <el-icon class='tw-ml-[5px]'>{this.collapse ? <ArrowUp /> : <ArrowDown />}</el-icon>
        </el-button>
      );
    };

    // 渲染固定头部
    const renderFHeader = () => {
      const { row = {}, col } = this.fheader;
      const fheaderVNode = this.$slots.fheader?.({ collapse: this.collapse });

      if (fheaderVNode instanceof Array) {
        fheaderVNode.forEach((vNodeItem: any) => {
          const vNodeChildKeys = Object.keys(vNodeItem.children || {});
          if (vNodeItem.type?.name === 'ElForm') {
            vNodeChildKeys.forEach((key) => {
              const _renderFunc = vNodeItem.children[key];

              if (typeof _renderFunc !== 'function') {
                return;
              }

              vNodeItem.children[key] = function renderDef() {
                const children = _renderFunc();
                // 给row的数据
                const rowPropsData = {
                  gutter: 10,
                  ...row,
                };

                const _renderChild = () => {
                  // 这块处理展开按钮
                  {
                    const _getShowCollapseVNode = (vNode: any) => {
                      const childs = vNode.children.default();
                      vNode.children.default = () => {
                        return [...childs, renderCollapse()];
                      };

                      return vNode;
                    };

                    let len = children?.length || 0;
                    while (len > 0) {
                      len -= 1;
                      const lastElFormItemChild = children[len];
                      if (
                        lastElFormItemChild?.type?.componentName === 'ElFormItem'
                        // 保证最后一个form-item可被正确渲染
                        && typeof lastElFormItemChild?.children?.default === 'function'
                      ) {
                        _getShowCollapseVNode(lastElFormItemChild);
                        break;
                      }
                    }
                  }

                  const colClass = getColClassList(col || { xs: 24, sm: 12, md: 12, lg: 8, xl: 8 }, rowPropsData);

                  const colStyle = getColStyle(rowPropsData);

                  return children.map((vNode: any) => {
                    return vNode?.type?.componentName !== 'ElFormItem' ? (
                      vNode
                    ) : (
                      <div
                        class={['el-col', colClass]}
                        style={colStyle}
                      >
                        {vNode}
                      </div>
                    );
                  });
                };

                return [<el-row {...rowPropsData}>{_renderChild()}</el-row>];
              };
            });
          }
        });
      }

      return (
        <el-header
          class={[
            'page-fixed-header',
            'tw-relative',
            'tw-pt-15px',
            'tw-px-10px',
            'tw-bg-white',
            'tw-rounded-4px',
            'tw-mb-12px'
          ]}
          style={{
            backgroundColor: this.fheaderBg,
            padding: this.fheaderPadding
          }}
          v-show={!!fheaderVNode}
        >
          {
            /**
             * 保证vue node节点插入
             */
            fheaderVNode || <div />
          }
        </el-header>
      );
    };

    // render Main header
    const renderMainHeader = () => {
      if (!this.$slots.header) return null;
      this.windowResizeCallBack();
      return (
        <el-header
          class='tw-pb-16px tw-relative'
          ref='contentHeaderRef'
        >
          {this.$slots.header()}
        </el-header>
      );
    };

    // 渲染主要内容区域
    const renderMain = () => {
      return (
        <el-main
          class='tw-h-100% tw-rounded-4px tw-relative'
          ref='contentWrapperRef'
          style={{
            backgroundColor: this.mainBg,
            padding: this.mainPadding
          }}
        >
          {this.$slots.main && (
            <el-main
              ref='contentMainRef'
              class='tw-h-100%'
            >
              {renderMainHeader()}

              {this.$slots.main({ height: this?.contentHeight || 0 })}

              {this.$slots.footer && (
                <el-footer
                  class='tw-flex tw-justify-end tw-items-center tw-p-0'
                  ref='contentFooterRef'
                >
                  {this.$slots.footer()}
                </el-footer>
              )}
            </el-main>
          )}
        </el-main>
      );
    };

    // 渲染固定底部
    const renderFFooter = () => {
      if (!this.$slots.ffooter) return null;

      return (
        <el-footer
          class={[
            'app-page-ffooter',
            'tw-flex',
            'tw-h-auto',
            'tw-justify-between',
            'tw-items-center',
            'tw-px-24px',
            'tw-py-20px',
            'tw-bg-white',
            'tw-overflow-x-auto',
            'tw-rounded-b-4px',
            'tw-border-t-1px',
            'tw-border-t-solid',
            'tw-border-t-[#D8DBE6]'
          ]}
        >
          {this.$slots.ffooter()}
        </el-footer>
      );
    };

    // 渲染左侧边栏
    const renderLAside = () => {
      if (!this.$slots.laside) return null;
      const laside = this.laside || {};
      return (
        <el-aside
          class={[
            'tw-p-15px',
            'tw-overflow-x-hidden',
            'tw-bg-white',
            'tw-rounded-[4px]',
            'tw-mr-12px'
          ]}
          {...laside}
        >
          {this.$slots.laside()}
        </el-aside>
      );
    };

    // 渲染右侧边栏
    const renderRAside = () => {
      if (!this.$slots.raside) return null;
      const raside = this.raside || {};
      return (
        <el-aside
          class={[
            'tw-p-15px',
            'tw-overflow-x-hidden',
            'tw-bg-white',
            'tw-rounded-[4px]',
            'tw-ml-12px'
          ]}
          {...raside}
        >
          {this.$slots.raside()}
        </el-aside>
      );
    };
    return (
      <div
        class='app-page'
      >
        {this.$slots.default && this.$slots.default()}

        <el-container>
          {renderLAside()}

          <el-container
            class='tw-w-auto'
            direction='vertical'
          >
            {renderFHeader()}

            {renderMain()}

            {renderFFooter()}
          </el-container>

          {renderRAside()}
        </el-container>
      </div>
    );
  },
});
</script>

<style lang="scss" scoped>
.app-page {
  overflow-x: auto;
  width: 100%;
  height: 100%;
  :deep(.el-card__body) {
    height: 100%;
    padding: 0;
  }
  .page-fixed-header {
    height: auto !important;
    :deep(.el-form) {
      width: 100%;
    }
    :deep(.el-form-item) {
      margin-bottom: 15px;
      &.label-hidden {
        .el-form-item__label {
          display: none;
        }
        .el-form-item__content {
          margin-left: 0 !important;
        }
      }
    }
  }
}
</style>
